import { api_configuration } from "../../common";
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import api_cs = api_configuration.types.client_state.alan_api;
import { client_state } from "../state/ClientState";
import { node_path, ui_scalar_value_expression, StringLike } from "../state/expressions";
import { variable_assignment, Variable } from "../state/Variable";
import { Scalar, onScalarValue, createExpressionBuilder, createVariable } from "../../../../../stream";
import { Pending, Failed, Unknown, isResolved, Result } from "../Status";
import { translate } from "../state/phrases";

export function initializedNumericalTypeProperties(
	numerical_type_properties: api_cs.Cnumerical_type_properties
): api_cs.Cyes__initialized__numerical_type_properties {
	return numerical_type_properties.properties.initialized.ensure("yes", {
		decimals: 0,
		unit: "",
	});
}

type Initialized = {
	__initialized: boolean;
};

export function evaluateNumericalTypeProperty(
	numerical_type_properties: api_cs.Cyes__initialized__numerical_type_properties,
	number_property: api_app.Cnumber__type__property,
	this_node: api_cs.Cnode
): void {
	const initialized_properties = numerical_type_properties as unknown as Initialized;
	if (initialized_properties.__initialized) {
		return;
	}
	initialized_properties.__initialized = true;
	const numerical_type = number_property.properties.numerical_type.ref;
	let path: api_app.Cnode_path | null = null;
	switch (number_property.properties.ui.properties.dynamic_numerical_type.state.name) {
		case "no":
			break;
		case "yes": {
			const bind_to_numerical_type = number_property.properties.ui.properties.dynamic_numerical_type.state.node;
			path = bind_to_numerical_type.properties.binding_path;
			break;
		}
	}
	evaluateNumericalType(path, numerical_type_properties, numerical_type, this_node);
}

export function evaluateNumericalTypeFilter(
	numerical_type_properties: api_cs.Cyes__initialized__numerical_type_properties,
	number_filter: api_cs.Cyes__has_filter__number
): void {
	const initialized_properties = numerical_type_properties as unknown as Initialized;
	if (initialized_properties.__initialized) {
		return;
	}
	initialized_properties.__initialized = true;
	const number_property = number_filter.parent.inferences.number_type().properties.number.ref.inferences.number();
	const numerical_type = number_property.properties.numerical_type.ref;

	switch (number_property.properties.ui.properties.dynamic_numerical_type.state.name) {
		case "no":
			evaluateNumericalType(null, numerical_type_properties, numerical_type, null);
			break;
		case "yes":
			return; //Dynamical numerical types not supported for filters
	}
}

function setUnit(
	input: Scalar<string>,
	numerical_type_properties: api_cs.Cyes__initialized__numerical_type_properties
) {
	onScalarValue(
		input,
		(value) => {
			numerical_type_properties.set_unit(value);
		},
		(scalar, subscription) => numerical_type_properties.onDestroy(() => scalar.unsubscribe(subscription))
	);
}

function setDecimals(
	input: Scalar<Result<number>>,
	numerical_type_properties: api_cs.Cyes__initialized__numerical_type_properties
) {
	onScalarValue(
		input,
		(value) => {
			switch (value) {
				case Pending:
					break;
				case Unknown:
				case Failed:
					numerical_type_properties.set_decimals(0);
					break;
				default:
					numerical_type_properties.set_decimals(value);
			}
		},
		(scalar, subscription) => numerical_type_properties.onDestroy(() => scalar.unsubscribe(subscription))
	);
}

function evaluateNumericalType(
	binding_path: api_app.Cnode_path | null,
	numerical_type_properties: api_cs.Cyes__initialized__numerical_type_properties,
	numerical_type: api_app.Cnumerical_types,
	this_node: api_cs.Cnode | null
) {
	switch (numerical_type.properties.ui.properties.represent_as.state.name) {
		case "model":
			setUnit(translate(numerical_type.key), numerical_type_properties);
			break;
		case "date":
			setUnit(translate(numerical_type.key), numerical_type_properties);
			break;
		case "date and time":
			setUnit(translate(numerical_type.key), numerical_type_properties);
			break;
		case "duration":
			setUnit(translate(numerical_type.key), numerical_type_properties);
			break;
		case "custom type": {
			const $ = numerical_type.properties.ui.properties.represent_as.state.node;
			const root_context = client_state.properties.root;
			const variable_context = createVariable<Variable | null | Failed>(
				$.properties.binding.switch({
					dynamic: ($dynamic) => {
						if (binding_path === null || this_node === null) {
							return Failed;
						} else {
							return node_path(binding_path).pipe((context) =>
								isResolved(context) ? variable_assignment($dynamic.properties.assignment, null, context) : Failed
							)([this_node, null]);
						}
					},
					root: () => null,
				}),
				false
			);
			setUnit(
				createExpressionBuilder(variable_context).pipe((variable) => {
					if (variable === Failed) return numerical_type.key;
					return ui_scalar_value_expression<StringLike>($.properties.properties.properties.label)
						.pipe((expression_result) =>
							isResolved(expression_result) ? expression_result.toString() : numerical_type.key
						)
						.pipe(translate)([root_context, variable]);
				})(),
				numerical_type_properties
			);
			switch ($.properties.properties.properties.conversion.state.name) {
				case "none":
					break;
				case "point translation": {
					const point_translation = $.properties.properties.properties.conversion.state.node;
					setDecimals(
						createExpressionBuilder(variable_context).pipe((variable) => {
							if (variable === Failed) return 0;
							return ui_scalar_value_expression<number>(point_translation.properties.decimals)([
								root_context,
								variable,
							]);
						})(),
						numerical_type_properties
					);
					break;
				}
			}
			break;
		}
	}
}
